import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import {
	BACKGROUND_REQUEST_HEADER,
	BACKGROUND_REQUEST_HEADER_VALUE,
	SessionStatusPayload,
} from '@dealstack/shared/lib/common/session/sessionContract';

/**
 * Reading status is always a background call: it is made on a timer, and counting it
 * as activity would mean a session could never go idle. The server enforces this too;
 * the header is here so the intent is visible at the call site.
 */
const BACKGROUND_HEADERS = { [BACKGROUND_REQUEST_HEADER]: BACKGROUND_REQUEST_HEADER_VALUE };

// Responses come wrapped by the API's response interceptor. Reading the fields off the
// envelope instead of the payload yields undefined deadlines, which the countdown then
// compares as NaN and silently never warns.
const unwrap = (response: ApiV1.Response<SessionStatusPayload>): SessionStatusPayload => {
	if (!response.payload) {
		throw new Error('Session status response contained no payload');
	}

	return response.payload;
};

export const getSessionStatus = async (): Promise<SessionStatusPayload> =>
	unwrap(
		await makeRequest<ApiV1.Response<SessionStatusPayload>>(
			{
				endpoint: API_PATHS.AUTH_SESSION_GET,
				method: HTTP_METHOD.GET,
				customHeaders: BACKGROUND_HEADERS,
			},
			// The caller decides what an expired session means; it must not trigger a
			// redirect from underneath the warning dialog.
			{ skipLoginRedirect: true }
		)
	);

/**
 * Deliberate "keep me signed in". Not marked as background: the user asked for it, and
 * the server slides the idle window forward on the strength of that.
 */
export const keepSessionAlive = async (): Promise<SessionStatusPayload> =>
	unwrap(
		await makeRequest<ApiV1.Response<SessionStatusPayload>>(
			{
				endpoint: API_PATHS.AUTH_SESSION_KEEP_ALIVE_POST,
				method: HTTP_METHOD.POST,
			},
			{ skipLoginRedirect: true }
		)
	);
